import React, { Component } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItemButton,
    ListItemIcon
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';

// A working copy of the columns, so ticking a box changes nothing in the grid
// until Save. The dialog used to flip `displayGrid` on the grid's own column
// objects, so Cancel kept every change it was meant to throw away.
const copyHead = (head = []) => head.map((column) => ({ ...column }));

class TableSettingsDialog extends Component {
    //props: {open: boolean, onClose: function, Head: array, Name: string, onSave: function, onResetLayout?: function, canResetLayout?: boolean}
    constructor(props) {
        super(props);
        this.state = { head: copyHead(this.props.Head) };
    }

    componentDidUpdate(prevProps) {
        // The dialog stays mounted between openings: start each one from the
        // grid's current columns, not from what was left unsaved last time.
        if (this.props.open && !prevProps.open) {
            this.setState({ head: copyHead(this.props.Head) });
        }
    }

    handleTableSettings = (id) => {
        this.setState(({ head }) => ({
            head: head.map((column) =>
                column.id === id
                    ? { ...column, displayGrid: !column.displayGrid }
                    : column
            )
        }));
    };

    handleSelectAll = () => {
        this.setState(({ head }) => {
            const selectAll = !head.every((column) => column.displayGrid);
            return {
                head: head.map((column) => ({
                    ...column,
                    displayGrid: selectAll
                }))
            };
        });
    };

    handleSave = () => {
        const merged = mergeHeadWithStorage(this.props.Name, this.props.Head);

        const userById = new Map((this.state.head || []).map((c) => [c.id, c]));
        const finalHead = (merged || []).map((col) => {
            const u = userById.get(col.id);
            if (u && typeof u.displayGrid === 'boolean') {
                return { ...col, displayGrid: u.displayGrid };
            }
            return col;
        });

        localStorage.setItem(
            `head_${this.props.Name}`,
            JSON.stringify(finalHead)
        );
        this.props.onSave();
    };

    render() {
        const { head } = this.state;
        const shown = head.filter((column) => column.displayGrid).length;
        const allShown = head.length > 0 && shown === head.length;

        return (
            <Dialog open={this.props.open} onClose={this.props.onClose}>
                <DialogTitle>{i18n.t('common.tableSettings')}</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>
                        {i18n.t('text.tableSettingsDescription')}
                    </DialogContentText>
                    <List>
                        <ListItemButton dense onClick={this.handleSelectAll}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={allShown}
                                    indeterminate={shown > 0 && !allShown}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{
                                        'aria-labelledby': 'selectAll'
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id="selectAll"
                                primary={i18n.t('common.selectAll')}
                            />
                        </ListItemButton>
                        <Divider sx={{ my: 1 }} />
                        {head.map((column) => (
                            <ListItemButton
                                key={column.id}
                                dense
                                onClick={() =>
                                    this.handleTableSettings(column.id)
                                }
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={!!column.displayGrid}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{
                                            'aria-labelledby': column.id
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    id={column.id}
                                    primary={i18n.t(`columns.${column.label}`)}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                    {/* Widths and order are not part of the working copy:
                        the reset applies at once and offers its own undo. */}
                    {this.props.onResetLayout && (
                        <Button
                            size="small"
                            startIcon={<Iconify icon="eva:refresh-outline" />}
                            onClick={this.props.onResetLayout}
                            disabled={!this.props.canResetLayout}
                            sx={{ mt: 1 }}
                        >
                            {i18n.t('common.resetColumnLayout')}
                        </Button>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={this.props.onClose}>
                        {i18n.t('buttons.cancel')}
                    </Button>
                    <Button variant="contained" onClick={this.handleSave}>
                        {i18n.t('buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default TableSettingsDialog;
